import { useState, useEffect, useRef } from 'react';
import { AlertCircle, Calendar, Mail, Zap } from 'lucide-react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { usePreviewDigest } from '@/hooks/useSecretary';

const DATE_RANGE_OPTIONS = [
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' }
];

export default function GenerateDigestDialog({ isOpen, onClose, onGenerate, sinceReportId }) {
  const [days, setDays] = useState(7);
  const [maxEmails, setMaxEmails] = useState(50);
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const previewMutation = usePreviewDigest();
  const loadingRef = useRef(false);

  // Load preview when dialog opens or options change
  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setIsLoadingPreview(false);
      loadingRef.current = false;
      return;
    }

    // Prevent multiple simultaneous requests
    if (loadingRef.current) {
      return;
    }

    console.log('Loading preview with:', { sinceReportId, days });

    const loadPreview = async () => {
      loadingRef.current = true;
      setIsLoadingPreview(true);

      try {
        const result = await previewMutation.mutateAsync({
          sinceReportId,
          days: sinceReportId ? undefined : days
        });
        console.log('Preview result:', result);
        setPreview(result);
      } catch (error) {
        console.error('Failed to load preview:', error);
        setPreview(null);
      } finally {
        setIsLoadingPreview(false);
        loadingRef.current = false;
      }
    };

    loadPreview();
  }, [isOpen, days, sinceReportId]); // Don't include previewMutation - it changes on every render

  const handleGenerate = () => {
    onGenerate({
      sinceReportId,
      days: sinceReportId ? undefined : days,
      maxEmails
    });
    onClose();
  };

  const isHighTokenUsage = preview && preview.estimated_tokens > 5000;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Boswell Brief"
      size="md"
    >
      <div className="space-y-4">
        {/* Date Range Selection (only if no sinceReportId) */}
        {!sinceReportId && (
          <div>
            <label className="block text-sm font-medium mb-2">
              <Calendar className="h-4 w-4 inline mr-2" />
              Time Period
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DATE_RANGE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => setDays(option.value)}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    days === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card hover:bg-accent border-input'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preview Information */}
        {isLoadingPreview ? (
          <div className="p-6 text-center">
            <LoadingSpinner />
            <p className="text-sm text-muted-foreground mt-2">Loading preview...</p>
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Emails in period:</span>
                <span className="text-lg font-bold">{preview.email_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Will analyze:</span>
                <span className="text-lg font-bold text-primary">{preview.emails_to_analyze}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estimated tokens:</span>
                <span className={`text-lg font-bold ${isHighTokenUsage ? 'text-orange-600' : 'text-green-600'}`}>
                  ~{preview.estimated_tokens.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Warning for high token usage */}
            {isHighTokenUsage && (
              <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-orange-800 dark:text-orange-300">High token usage</p>
                  <p className="text-orange-700 dark:text-orange-400 mt-1">
                    This will use significant AI tokens. Consider reducing the analysis limit below.
                  </p>
                </div>
              </div>
            )}

            {/* Warning if not all emails will be analyzed */}
            {preview.warning && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-800 dark:text-blue-300">{preview.warning}</p>
                </div>
              </div>
            )}

            {/* Max Emails Adjustment */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <Zap className="h-4 w-4 inline mr-2" />
                Analysis Limit
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="10"
                  value={maxEmails}
                  onChange={(e) => setMaxEmails(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16 text-right">{maxEmails} emails</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Analyzing more emails provides better insights but uses more tokens. Est. ~{(maxEmails * 150).toLocaleString()} tokens
              </p>
            </div>
          </div>
        ) : null}

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isLoadingPreview || !preview}
          >
            <Zap className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
        </div>
      </div>
    </Modal>
  );
}
